import { useCallback } from 'react';
import { Link } from 'react-router-dom';

const CSV_TEMPLATE = `nome,telefone,email,grupo
João Silva,555499999999,joao@email.com,Clientes
Maria Souza,555498888888,maria@email.com,Leads
Carlos Oliveira,555197777777,carlos@email.com,Orçamentos
Ana Santos,555196666666,ana@email.com,Compradores
Pedro Lima,555195555555,pedro@email.com,Pós-venda
`;

function handleDownloadTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'modelo-contatos-wzap.csv';
  link.click();
}

export default function ImportTutorialPage() {
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Hero */}
      <div className="relative rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700" />
        <div className="relative px-7 py-9 md:px-10 md:py-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-white text-xs font-medium mb-4">
                <i className="ri-guide-line" />
                Tutorial
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                Como importar seus contatos do WhatsApp para o WZAP
              </h2>
              <p className="text-white/80 text-sm max-w-[580px] leading-relaxed">
                Para criar campanhas e enviar mensagens para seus clientes, primeiro você precisa importar sua lista de contatos.
                Siga o passo a passo abaixo e comece a usar o WZAP em minutos.
              </p>
            </div>
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-2 px-5 py-3 bg-white hover:bg-background-50 text-primary-700 font-semibold text-sm rounded-xl transition-all cursor-pointer whitespace-nowrap flex-shrink-0 shadow-lg shadow-primary-700/30"
            >
              <i className="ri-download-line text-lg" />
              Baixar modelo CSV
            </button>
          </div>
        </div>
      </div>

      {/* Step 1 — Exportar contatos do celular */}
      <section className="bg-white rounded-2xl border border-background-200 overflow-hidden">
        <div className="px-7 py-6 border-b border-background-100">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-base font-heading font-bold text-primary-600">1</span>
            </div>
            <div>
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">
                Exportar os contatos do seu celular
              </h3>
              <p className="text-sm text-foreground-500">
                O primeiro passo é extrair seus contatos do celular em formato .VCF (vCard).
              </p>
            </div>
          </div>
        </div>
        <div className="px-7 py-6 grid md:grid-cols-2 gap-6">
          {/* Android */}
          <div className="rounded-xl border border-background-200 bg-background-50/50 p-5">
            <div className="flex items-center gap-3 mb-3">
              <i className="ri-android-line text-2xl text-emerald-600" />
              <h4 className="font-heading font-semibold text-foreground-900 text-sm">Android</h4>
            </div>
            <ol className="space-y-2.5 text-sm text-foreground-600">
              <li className="flex gap-2">
                <span className="text-primary-500 font-bold flex-shrink-0">1.</span>
                Abra o aplicativo <strong>Contatos</strong> do seu celular.
              </li>
              <li className="flex gap-2">
                <span className="text-primary-500 font-bold flex-shrink-0">2.</span>
                Clique em <strong>Menu ☰ → Gerenciar contatos → Exportar contatos</strong>
              </li>
              <li className="flex gap-2">
                <span className="text-primary-500 font-bold flex-shrink-0">3.</span>
                Escolha <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-md text-xs font-semibold"><i className="ri-check-line" /> Exportar para arquivo .VCF</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary-500 font-bold flex-shrink-0">4.</span>
                Salve o arquivo no celular e envie para seu computador via:
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {['Google Drive', 'E-mail', 'WhatsApp Web', 'Cabo USB'].map((m) => (
                    <span key={m} className="px-2 py-0.5 bg-background-100 text-foreground-600 rounded-md text-xs">{m}</span>
                  ))}
                </div>
              </li>
            </ol>
          </div>

          {/* iPhone */}
          <div className="rounded-xl border border-background-200 bg-background-50/50 p-5">
            <div className="flex items-center gap-3 mb-3">
              <i className="ri-apple-line text-2xl text-foreground-600" />
              <h4 className="font-heading font-semibold text-foreground-900 text-sm">iPhone</h4>
            </div>
            <ol className="space-y-2.5 text-sm text-foreground-600">
              <li className="flex gap-2">
                <span className="text-primary-500 font-bold flex-shrink-0">1.</span>
                Acesse <strong>Ajustes → seu nome → iCloud</strong>
              </li>
              <li className="flex gap-2">
                <span className="text-primary-500 font-bold flex-shrink-0">2.</span>
                Ative <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-md text-xs font-semibold"><i className="ri-check-line" /> Contatos</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary-500 font-bold flex-shrink-0">3.</span>
                Acesse o <strong>iCloud pelo computador</strong> em{' '}
                <a href="https://www.icloud.com" target="_blank" rel="nofollow noopener" className="text-primary-600 hover:underline">icloud.com</a>
              </li>
              <li className="flex gap-2">
                <span className="text-primary-500 font-bold flex-shrink-0">4.</span>
                Exporte os contatos como <strong>vCard (.VCF)</strong>
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* Step 2 — Converter VCF para CSV */}
      <section className="bg-white rounded-2xl border border-background-200 overflow-hidden">
        <div className="px-7 py-6 border-b border-background-100">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-base font-heading font-bold text-primary-600">2</span>
            </div>
            <div>
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">
                Converter o arquivo para CSV
              </h3>
              <p className="text-sm text-foreground-500">
                O WZAP trabalha com arquivo CSV, que é o formato usado para importar listas.
              </p>
            </div>
          </div>
        </div>
        <div className="px-7 py-6 space-y-5">
          {/* Format preview table */}
          <div>
            <p className="text-sm font-semibold text-foreground-800 mb-3">O arquivo deve ficar assim:</p>
            <div className="overflow-x-auto rounded-xl border border-background-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground-600 uppercase tracking-wider">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground-600 uppercase tracking-wider">Telefone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground-600 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground-600 uppercase tracking-wider">Grupo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-100">
                    <td className="px-4 py-3 text-foreground-800">João Silva</td>
                    <td className="px-4 py-3 text-foreground-600 font-mono text-xs">555499999999</td>
                    <td className="px-4 py-3 text-foreground-600">joao@email.com</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                        <i className="ri-group-line" />Clientes
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-foreground-800">Maria Souza</td>
                    <td className="px-4 py-3 text-foreground-600 font-mono text-xs">555498888888</td>
                    <td className="px-4 py-3 text-foreground-600">maria@email.com</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                        <i className="ri-group-line" />Leads
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Phone format */}
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-amber-600 text-lg flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-2">Formato do telefone</p>
                <p className="text-sm text-amber-700">
                  Use sempre o número <strong>com código do país (DDI)</strong>:
                </p>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-white rounded-lg border border-amber-200 text-sm font-semibold text-foreground-800">
                    <i className="ri-flag-line text-emerald-500" />Brasil: 55 + DDD + número
                  </span>
                  <code className="px-3 py-2 bg-white rounded-lg border border-amber-200 text-sm font-mono text-primary-600">
                    555499999999
                  </code>
                </div>
              </div>
            </div>
          </div>

          {/* Converter tools */}
          <div>
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Ferramentas gratuitas para conversão</p>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'vcf-to-csv.com', url: 'https://vcf-to-csv.com' },
                { name: 'convertio.co', url: 'https://convertio.co' },
                { name: 'aconvert.com', url: 'https://www.aconvert.com' },
              ].map((tool) => (
                <a
                  key={tool.name}
                  href={tool.url}
                  target="_blank"
                  rel="nofollow noopener"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-background-50 hover:bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-external-link-line text-xs" />
                  {tool.name}
                </a>
              ))}
            </div>
          </div>

          {/* Download template button */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200">
            <div>
              <p className="text-sm font-semibold text-foreground-800">Modelo pronto para usar</p>
              <p className="text-xs text-foreground-500 mt-0.5">Baixe o arquivo modelo, preencha com seus dados e importe direto no WZAP.</p>
            </div>
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 sm:ml-auto"
            >
              <i className="ri-download-line text-lg" />
              Baixar modelo CSV
            </button>
          </div>
        </div>
      </section>

      {/* Step 3 — Entrar no WZAP */}
      <section className="bg-white rounded-2xl border border-background-200 overflow-hidden">
        <div className="px-7 py-6 border-b border-background-100">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-base font-heading font-bold text-primary-600">3</span>
            </div>
            <div>
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">
                Entrar no WZAP
              </h3>
              <p className="text-sm text-foreground-500">
                Acesse o painel e vá até a seção de contatos.
              </p>
            </div>
          </div>
        </div>
        <div className="px-7 py-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-1 space-y-3 text-sm text-foreground-600">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-primary-600 text-xs" />
                </div>
                <span>Entre com seu usuário e senha no <strong>Painel WZAP</strong></span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-contacts-book-line text-primary-600 text-xs" />
                </div>
                <span>Depois vá em <strong>Contatos → Importar</strong></span>
              </div>
            </div>
            <Link
              to="/contacts"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              <i className="ri-contacts-book-line text-lg" />
              Ir para Contatos
            </Link>
          </div>
        </div>
      </section>

      {/* Step 4 — Enviar o arquivo */}
      <section className="bg-white rounded-2xl border border-background-200 overflow-hidden">
        <div className="px-7 py-6 border-b border-background-100">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-base font-heading font-bold text-primary-600">4</span>
            </div>
            <div>
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">
                Enviar o arquivo
              </h3>
              <p className="text-sm text-foreground-500">
                Faça o upload do CSV e deixe o sistema analisar seus contatos.
              </p>
            </div>
          </div>
        </div>
        <div className="px-7 py-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-upload-cloud-2-line text-primary-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">Clique em &ldquo;Selecionar arquivo CSV&rdquo;</p>
                  <p className="text-xs text-foreground-500 mt-0.5">Escolha o arquivo no seu computador.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-search-line text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">O sistema irá analisar</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
                      <i className="ri-check-line" />Quantidade de contatos
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
                      <i className="ri-check-line" />Números válidos
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium">
                      <i className="ri-contrast-2-line" />Contatos duplicados
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
                      <i className="ri-check-line" />Campos preenchidos
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Visual card */}
            <div className="w-full md:w-[220px] flex-shrink-0 rounded-xl border-2 border-dashed border-background-200 bg-background-50 p-5 flex flex-col items-center justify-center text-center">
              <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mb-3">
                <i className="ri-file-text-line text-xl text-primary-500" />
              </div>
              <p className="text-sm font-semibold text-foreground-800">modelo-contatos.csv</p>
              <p className="text-[11px] text-foreground-400 mt-1">Arraste o arquivo aqui ou clique para selecionar</p>
            </div>
          </div>
        </div>
      </section>

      {/* Step 5 — Organizar contatos */}
      <section className="bg-white rounded-2xl border border-background-200 overflow-hidden">
        <div className="px-7 py-6 border-b border-background-100">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-base font-heading font-bold text-primary-600">5</span>
            </div>
            <div>
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">
                Organizar seus contatos
              </h3>
              <p className="text-sm text-foreground-500">
                Após importar, você pode criar grupos e tags para segmentar seus contatos.
              </p>
            </div>
          </div>
        </div>
        <div className="px-7 py-6">
          <div className="flex flex-wrap gap-3">
            {[
              { label: 'Clientes', color: 'bg-accent-100 text-accent-700 border-accent-200', icon: 'ri-user-star-line' },
              { label: 'Leads', color: 'bg-secondary-100 text-secondary-700 border-secondary-200', icon: 'ri-user-received-line' },
              { label: 'Orçamentos', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: 'ri-calculator-line' },
              { label: 'Compradores', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: 'ri-shopping-cart-line' },
              { label: 'Pós-venda', color: 'bg-rose-100 text-rose-700 border-rose-200', icon: 'ri-heart-line' },
            ].map((group) => (
              <div
                key={group.label}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium ${group.color}`}
              >
                <i className={`${group.icon} text-base`} />
                {group.label}
              </div>
            ))}
          </div>
          <p className="text-xs text-foreground-400 mt-4">
            Use grupos e tags para criar campanhas direcionadas e personalizadas por segmento.
          </p>
        </div>
      </section>

      {/* Step 6 — Criar campanhas */}
      <section className="bg-white rounded-2xl border border-background-200 overflow-hidden">
        <div className="px-7 py-6 border-b border-background-100">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-base font-heading font-bold text-primary-600">6</span>
            </div>
            <div>
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">
                Criar campanhas
              </h3>
              <p className="text-sm text-foreground-500">
                Com os contatos cadastrados, é hora de criar sua primeira campanha de WhatsApp.
              </p>
            </div>
          </div>
        </div>
        <div className="px-7 py-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
            {[
              { label: 'Lista de contatos', icon: 'ri-contacts-book-line' },
              { label: 'Mensagem de texto', icon: 'ri-message-2-line' },
              { label: 'Imagem', icon: 'ri-image-line' },
              { label: 'Vídeo', icon: 'ri-video-line' },
              { label: 'Documento PDF', icon: 'ri-file-pdf-2-line' },
              { label: 'Arquivo', icon: 'ri-attachment-line' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-background-200 bg-background-50/50">
                <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-primary-500 text-base`} />
                </div>
                <span className="text-sm font-medium text-foreground-700">{item.label}</span>
              </div>
            ))}
          </div>

          {/* Example message */}
          <div className="rounded-xl bg-background-50 border border-background-200 p-4">
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Exemplo de mensagem</p>
            <div className="bg-white rounded-lg border border-background-200 p-4 max-w-sm">
              <p className="text-sm text-foreground-800 leading-relaxed">
                Olá <strong>João</strong>!
              </p>
              <p className="text-sm text-foreground-600 mt-1 leading-relaxed">
                Temos uma condição especial para você.
              </p>
              <p className="text-sm text-foreground-600 mt-1 leading-relaxed">
                Confira nossa oferta: <span className="text-primary-600 font-medium">oferta.wzap.pro/joao</span>
              </p>
            </div>
          </div>

          <Link
            to="/campaigns"
            className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-megaphone-line text-lg" />
            Criar nova campanha
          </Link>
        </div>
      </section>

      {/* Important: segurança WhatsApp */}
      <section className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 overflow-hidden">
        <div className="px-7 py-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-amber-600 text-lg" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">
                Importante sobre envio de mensagens
              </h3>
              <p className="text-sm text-foreground-500">
                Para manter seu WhatsApp seguro e evitar bloqueios, siga estas recomendações.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { icon: 'ri-user-heart-line', text: 'Use contatos de clientes reais' },
              { icon: 'ri-spam-line', text: 'Evite listas compradas' },
              { icon: 'ri-edit-line', text: 'Personalize mensagens' },
              { icon: 'ri-speed-up-line', text: 'Faça envios graduais' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3 px-4 py-3 bg-white/70 rounded-xl border border-amber-200/60">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-amber-600 text-sm`} />
                </div>
                <span className="text-sm font-medium text-foreground-700">{item.text}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-foreground-500 mt-4">
            O WZAP possui <strong>controle de velocidade de envio</strong> para ajudar a evitar bloqueios do WhatsApp.
          </p>
        </div>
      </section>

      {/* Google Contacts */}
      <section className="bg-white rounded-2xl border border-background-200 overflow-hidden">
        <div className="px-7 py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-google-line text-xl text-foreground-600" />
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold uppercase tracking-wide">
                  <i className="ri-check-line text-xs" />
                  Disponível
                </span>
              </div>
              <h4 className="text-base font-heading font-semibold text-foreground-900 mb-1">
                Importar contatos direto do Google Contatos
              </h4>
              <p className="text-sm text-foreground-500 max-w-[500px]">
                Muitas empresas já têm milhares de clientes salvos no Google. Conecte sua conta e importe seus contatos com apenas um clique, sem precisar exportar ou converter arquivos.
              </p>
            </div>
            <Link
              to="/contacts"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              <i className="ri-google-line text-lg" />
              Conectar Google
            </Link>
          </div>
        </div>
      </section>

      {/* Quick link back to contacts */}
      <div className="text-center pb-4">
        <Link
          to="/contacts"
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-foreground-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          Voltar para Contatos
        </Link>
      </div>
    </div>
  );
}